import * as React from "react";
import {
  PersonOutlined,
  NotificationsOutlined,
  DashboardOutlined,
  AssignmentOutlined,
  AssessmentOutlined,
  CalendarMonthOutlined,
  BusinessOutlined,
  ExpandLess,
  ExpandMore,
  InboxOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import BackHandOutlinedIcon from '@mui/icons-material/BackHandOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";
import { routeKeys } from "./listmap";
import { buildManagementChildPermissionCheck, matchPathPattern } from "../../../utils/routeAccess";
import { useDispatch, useSelector } from "react-redux";
import { open } from "../../../Redux/collapseSlice";

import { t } from "i18next";
import { Box } from "@mui/material";

/** Reserved for rare two-line submenu labels; prefer single-line + ellipsis for alignment. */
const SIDEBAR_MULTILINE_TITLES = {};

const NAV_ICON_SX = {
  width: 40,
  minWidth: 40,
  height: 40,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const NAV_ROW_BUTTON_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  minHeight: 48,
  py: 0,
};

const NAV_LABEL_SX = {
  flex: "1 1 auto",
  minWidth: 0,
  fontWeight: 600,
  fontSize: "0.875rem",
  lineHeight: "1.25rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
};

const SUB_NAV_LABEL_SX = {
  flex: "1 1 auto",
  minWidth: 0,
  fontWeight: 600,
  fontSize: "0.8125rem",
  lineHeight: "1.25rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
};

const SIDEBAR_TOOLTIP_SLOT_PROPS = {
  popper: {
    sx: {
      zIndex: (theme) => theme.zIndex.drawer + 2,
      pointerEvents: "none",
    },
  },
  tooltip: {
    sx: {
      maxWidth: 320,
      fontSize: "0.75rem",
    },
  },
};

function NavLabel({ text, sx }) {
  return (
    <Tooltip title={text} placement="right" arrow slotProps={SIDEBAR_TOOLTIP_SLOT_PROPS}>
      <Typography
        component="span"
        data-no-ellipsis-tooltip="true"
        sx={{ ...NAV_LABEL_SX, ...sx }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

function SubNavLabel({ text, selected = false }) {
  return (
    <Tooltip title={text} placement="right" arrow slotProps={SIDEBAR_TOOLTIP_SLOT_PROPS}>
      <Typography
        component="span"
        data-no-ellipsis-tooltip="true"
        sx={{
          ...SUB_NAV_LABEL_SX,
          color: selected ? "primary.main" : "text.primary",
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

function SubNavSelectionMarker({ active }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 3,
        height: 20,
        borderRadius: "2px",
        flexShrink: 0,
        alignSelf: "center",
        mr: 0.75,
        bgcolor: active ? "primary.main" : "transparent",
      }}
    />
  );
}

function SideList({ collapsed = false, tourEnabled = false }) {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authenticationValues.currentUser);
  const permissions = user.permissions;
  const isSuperAdmin = React.useMemo(() => {
    const roleList = user?.roles || [];
    if (!Array.isArray(roleList)) return false;
    return roleList.some((r) =>
      typeof r === "string" ? r === "Super Admin" : r?.name === "Super Admin"
    );
  }, [user?.roles]);
  const isAdmin = React.useMemo(() => {
    const roleList = user?.roles || [];
    if (!Array.isArray(roleList)) return false;
    return roleList.some((r) =>
      typeof r === "string" ? r === "Admin" : r?.name === "Admin"
    );
  }, [user?.roles]);
  const openManagement = useSelector(
    (state) => state.collapseValues.management
  );
  const openCommunication = useSelector(
    (state) => state.collapseValues.communication
  );
  const openPatients = useSelector(
    (state) => state.collapseValues.patients
  );
  const messagesUnreadCount = useSelector(
    (state) => state.notificationValues.messagesUnreadCount || 0
  );
  const navigate = useNavigate();
  const openValue = {
    openManagement: openManagement,
    openCommunication: openCommunication,
    openPatients: openPatients,
  };

  // State for collapsed menu
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeMenuKey, setActiveMenuKey] = React.useState(null);

  // Helper function to check if current location matches a route
  const isRouteActive = (routeKey) => {
    const location = window.location.pathname;
    const routes = routeKeys[routeKey] || [];

    return routes.some((route) => matchPathPattern(route, location));
  };

  // Helper function to check if any child route is active
  const isChildRouteActive = (children) => {
    return children.some(child => isRouteActive(child.key));
  };

  const listItemsAll = [
    {
      item: {
        key: "dashboard",
        text: t("dashboard"),
        multiLevel: false,
        collapse: null,
        icon: DashboardOutlined,
        childeren: [],
      },
    },
    {
      item: {
        key: "patients",
        text: "Patients",
        multiLevel: false,
        icon: PersonOutlined,
        collapse: "openPatients",
        childeren: [],
      },
    },
    {
      item: {
        key: "tasks",
        text: "Tasks",
        multiLevel: false,
        icon: AssignmentOutlined,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "case-days",
        text: "Procedure Days",
        multiLevel: false,
        icon: CalendarMonthOutlined,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "registry",
        text: "Registry",
        multiLevel: false,
        icon: BusinessOutlined,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "contacts",
        text: "Contacts",
        multiLevel: false,
        icon: PeopleOutlineOutlinedIcon,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "centerLocations",
        text: "Locations",
        multiLevel: false,
        icon: PlaceOutlined,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "inbox",
        text: "Inbox",
        multiLevel: false,
        icon: InboxOutlined,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "sarRequests",
        text: "SAR Requests",
        multiLevel: false,
        icon: PolicyOutlinedIcon,
        collapse: null,
        childeren: [],
      },
    },
    {
      item: {
        key: "communication",
        text: "Communication",
        multiLevel: true,
        icon: ForumOutlinedIcon,
        collapse: "openCommunication",
        childeren: [
          { key: "communicationMessages", text: "Messages", icon: MessageOutlinedIcon },
          { key: "communicationMessageTemplates", text: "Message Templates", icon: ArticleOutlinedIcon },
          { key: "communicationEmailTemplates", text: "Email Templates", icon: EmailOutlinedIcon },
          { key: "communicationCalls", text: "Calls", icon: PhoneOutlinedIcon },
          { key: "communicationModes", text: "Communication Modes", icon: SettingsOutlinedIcon },
        ],
      },
    },
    {
      item: {
        key: "management",
        text: "Management",
        multiLevel: true,
        icon: AdminPanelSettingsOutlinedIcon,
        collapse: "openManagement",
        childeren: [
          { key: "userPermissions", text: "Users", icon: ManageAccountsOutlinedIcon },
          { key: "rolePermissions", text: "Roles", icon: AdminPanelSettingsOutlinedIcon },
          { key: "centerPermissions", text: "Centers", icon: LocalHospitalOutlinedIcon },
          { key: "devicesPermissions", text: "Devices", icon: CableOutlinedIcon },
          { key: "medicationsPermissions", text: "Medications", icon: LocalPharmacyOutlinedIcon },
          { key: "medicationFrequenciesPermissions", text: "Medication Frequencies", icon: ScheduleOutlinedIcon },
          { key: "medicationHoldTimingsPermissions", text: "Medication Hold Timings", icon: ScheduleOutlinedIcon },
          { key: "insurancesPermissions", text: "Insurances", icon: HealthAndSafetyOutlinedIcon },
          { key: "indicationsPermissions", text: "Indications", icon: ErrorOutlineOutlinedIcon },
          { key: "specialtiesPermissions", text: "Specialties", icon: MedicalServicesOutlinedIcon },
          { key: "complicationsPermissions", text: "Complications", icon: BackHandOutlinedIcon },
          { key: "subjectAccessRequestsPermissions", text: "Subject Access Requests", icon: PolicyOutlinedIcon },
          { key: "contactDropdownFiltersPermissions", text: "Contact Dropdown Filters", icon: TuneOutlinedIcon },
        ],
      },
    },
    {
      item: {
        key: "pushNotification",
        text: t("notifications"),
        icon: NotificationsOutlined,
        multiLevel: false,
        collapse: null,
        childeren: [],
      },
    },
  ];

  const hasCommunicationAccess =
    isSuperAdmin ||
    permissions?.communication_modes?.read ||
    permissions?.communicationModes?.read;

  const managementChildPermissionCheck = buildManagementChildPermissionCheck(
    permissions,
    isSuperAdmin,
    isAdmin
  );

  const communicationChildPermissionCheck = {
    communicationMessages: hasCommunicationAccess,
    communicationMessageTemplates: hasCommunicationAccess,
    communicationEmailTemplates: hasCommunicationAccess,
    communicationCalls: hasCommunicationAccess,
    communicationModes: hasCommunicationAccess,
  };

  const listItems = listItemsAll
    .map((items) => {
      let filteredChild = items.item.childeren;

      if (items.item.key === "management") {
        filteredChild = items.item.childeren.filter(
          (child) => managementChildPermissionCheck[child.key]
        );
      } else if (items.item.key === "communication") {
        filteredChild = items.item.childeren.filter(
          (child) => communicationChildPermissionCheck[child.key]
        );
      }

      const permissionCheck = {
        dashboard: permissions?.analytics?.read,
        patients: permissions?.patients?.read,
        tasks: permissions?.tasks?.read,
        reports: permissions?.reports?.read,
        "case-days": permissions?.cases?.read,
        registry: permissions?.registry?.read,
        contacts: permissions?.contacts?.read,
        centerLocations: isSuperAdmin || permissions?.center_locations?.read,
        inbox: isSuperAdmin || permissions?.inbox?.read,
        sarRequests:
          isSuperAdmin ||
          isAdmin ||
          permissions?.subject_access_requests?.read,
        communicationMessages: hasCommunicationAccess,
        communicationMessageTemplates: hasCommunicationAccess,
        communicationEmailTemplates: hasCommunicationAccess,
        communicationCalls: hasCommunicationAccess,
        communicationModes: hasCommunicationAccess,
        communication:
          items.item.key === "communication" ? filteredChild.length > 0 : false,
        management:
          items.item.key === "management" ? filteredChild.length > 0 : false,
        pushNotification: permissions?.pushNotifications?.read,
      };

      const isVisible = permissionCheck[items.item.key];
      if (!isVisible) {
        return null;
      }

      return {
        item: {
          ...items.item,
          childeren:
            items.item.key === "management" || items.item.key === "communication"
              ? filteredChild
              : items.item.childeren,
        },
      };
    })
    .filter(Boolean);

  const location = window.location.pathname;
  const sxListItem = {
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.primary.veryLighter,
      color: theme.palette.primary.main,
      borderRadius: 1,
    },
    px: 0,
    mx: 0,
    pl: collapsed ? 0 : 2,
  };
  const mxListItem = {
    "&.Mui-selected, &.Mui-selected:hover": {
      color: theme.palette.primary.main,
    },
    mx: 0,
  };
  const subNavListItemSx = {
    ...mxListItem,
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
    },
    "&.Mui-selected:focus-visible": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  };

  const handleClick = (e, identifier) => {
    e.stopPropagation();
    e.ignore = true;
    switch (identifier) {
      case "management":
        dispatch(open({ management: !openManagement }));
        break;
      case "communication":
        dispatch(open({ communication: !openCommunication }));
        break;
      case "patients":
        dispatch(open({ patients: !openPatients }));
        break;
      default:
        break;
    }
  };

  const handleSelected = (e, identifier) => {
    if (!e.ignore) {
      if (
        identifier === "loyaltyMembers" &&
        !permissions?.loyaltyMemberPermissions?.read
      ) {
        navigate(routeKeys[identifier][1]);
      } else {
        navigate(routeKeys[identifier][0]);
      }
    }
  };

  // Handle collapsed menu for multilevel items
  const handleCollapsedMenuOpen = (event, menuKey) => {
    setAnchorEl(event.currentTarget);
    setActiveMenuKey(menuKey);
  };

  const handleCollapsedMenuClose = () => {
    setAnchorEl(null);
    setActiveMenuKey(null);
  };

  const handleCollapsedMenuItemClick = (childKey) => {
    handleSelected({ ignore: false }, childKey);
    handleCollapsedMenuClose();
  };

  // Collapsed mode rendering
  if (collapsed) {
    return (
      <>
        {listItems.map((value, index) => {
          const item = value.item;

          if (item.multiLevel) {
            // Multilevel item in collapsed mode - show as menu
            return (
              <Tooltip
                key={index}
                title={item.text}
                placement="right"
                arrow
                slotProps={SIDEBAR_TOOLTIP_SLOT_PROPS}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    data-tour={
                      tourEnabled
                        ? {
                            management: "sidenav-management",
                            communication: "sidenav-communication",
                          }[item.key]
                        : undefined
                    }
                    onClick={(e) => {
                      if (item.key === "communication") {
                        if (anchorEl && activeMenuKey === "communication") {
                          handleCollapsedMenuClose();
                          return;
                        }
                        dispatch(open({ communication: true }));
                        navigate(routeKeys.communicationMessages[0]);
                      }
                      handleCollapsedMenuOpen(e, item.key);
                    }}
                    selected={isRouteActive(item.key)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 45,
                      px: 1,
                      ...sxListItem,
                    }}
                  >
                    {isRouteActive(item.key) && (
                      <div style={{ backgroundColor: theme.palette.primary.main, position: "absolute", left: -1, top: "50%", transform: "translateY(-50%)", width: 3, height: 25, borderRadius: 2 }}>
                      </div>
                    )}
                    <ListItemIcon
                      sx={{
                        width: 40,
                        height: 25,
                        minWidth: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <item.icon
                          color={
                            isRouteActive(item.key) || isChildRouteActive(item.childeren)
                              ? "primary"
                              : "text"
                          }
                        />
                        {item.key === "communication" && messagesUnreadCount > 0 ? (
                          <Box
                            aria-hidden
                            sx={{
                              position: "absolute",
                              top: -2,
                              right: -2,
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "error.main",
                              border: "2px solid",
                              borderColor: "background.paper",
                              animation: "commUnreadDotBlink 1.2s ease-in-out infinite",
                              "@keyframes commUnreadDotBlink": {
                                "0%, 100%": { opacity: 1 },
                                "50%": { opacity: 0.25 },
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          } else {
            // Single level item in collapsed mode
            return (
              <Tooltip
                key={index}
                title={item.text}
                placement="right"
                arrow
                slotProps={SIDEBAR_TOOLTIP_SLOT_PROPS}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    data-tour={
                      tourEnabled
                        ? {
                            dashboard: "sidenav-dashboard",
                            patients: "sidenav-patients",
                            tasks: "sidenav-tasks",
                            "case-days": "sidenav-case-days",
                            registry: "sidenav-registry",
                            contacts: "sidenav-contacts",
                            messages: "sidenav-messages",
                          }[item.key]
                        : undefined
                    }
                    onClick={(e) => handleSelected(e, item.key)}
                    selected={isRouteActive(item.key)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 45,
                      px: 1,
                      ...sxListItem,
                    }}
                  >
                    {isRouteActive(item.key) && (
                      <div style={{ backgroundColor: theme.palette.primary.main, position: "absolute", left: -1, top: "50%", transform: "translateY(-50%)", width: 3, height: 25, borderRadius: 2 }}>
                      </div>
                    )}
                    <ListItemIcon
                      sx={{
                        width: 40,
                        height: 25,
                        minWidth: 40,
                        display: "flex",
                        justifyContent: "center",

                      }}
                    >
                      <item.icon
                        color={
                          isRouteActive(item.key)
                            ? "primary"
                            : "text"
                        }
                      />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          }
        })}

        {/* Menu for collapsed multilevel items */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCollapsedMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              zIndex: (theme) => theme.zIndex.drawer + 2,
            },
          }}
        >
          {activeMenuKey &&
            listItems
              .find(item => item.item.key === activeMenuKey)
              ?.item.childeren.map((childValue) => {
                const menuTitleLines = SIDEBAR_MULTILINE_TITLES[childValue.key];
                return (
                <MenuItem
                  key={childValue.key}
                  onClick={() => handleCollapsedMenuItemClick(childValue.key)}
                  selected={isRouteActive(childValue.key)}
                  sx={{
                    py: 1,
                    px: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: menuTitleLines ? "flex-start" : "center",
                    gap: 1,
                  }}
                >
                  {menuTitleLines ? (
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      {menuTitleLines.map((line, idx) => (
                        <Typography
                          key={idx}
                          component="span"
                          sx={{ display: "block", fontSize: "0.875rem", lineHeight: 1.28 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <span>{childValue.text}</span>
                  )}
                  {activeMenuKey === "communication" &&
                  childValue.key === "communicationMessages" &&
                  messagesUnreadCount > 0 ? (
                    <Chip
                      label={messagesUnreadCount}
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 22, minWidth: 22, flexShrink: 0, alignSelf: menuTitleLines ? "flex-start" : "center", mt: menuTitleLines ? 0.25 : 0 }}
                      color="primary"
                    />
                  ) : null}
                </MenuItem>
              );
              })
          }
        </Menu>
      </>
    );
  }

  // Full sidebar rendering (not collapsed)
  return (
    <>
      {listItems.map((value, index) => {
        return value.item.multiLevel ? (
          <Box
            key={index}
            sx={{ overflowX: "hidden", overflowY: "hidden", maxWidth: "100%" }}
            data-tour={
              tourEnabled
                ? {
                    management: "sidenav-management-section",
                    communication: "sidenav-communication-section",
                  }[value.item.key]
                : undefined
            }
          >
            <ListItem key={value.item.key} disablePadding>
              <ListItemButton
                data-tour={
                  tourEnabled
                    ? {
                        management: "sidenav-management",
                        communication: "sidenav-communication",
                      }[value.item.key]
                    : undefined
                }
                onClick={(e) => {
                  if (value.item.key === "communication") {
                    if (openCommunication) {
                      dispatch(open({ communication: false }));
                    } else {
                      dispatch(open({ communication: true }));
                      navigate(routeKeys.communicationMessages[0]);
                    }
                    return;
                  }
                  handleSelected(e, value.item.key);
                }}
                selected={isRouteActive(value.item.key)}
                sx={{
                  ...NAV_ROW_BUTTON_SX,
                  flexDirection: isRtl ? "row-reverse" : "row",
                  overflow: "hidden",
                  maxWidth: "100%",
                  ...sxListItem,
                  pl: 2,
                }}
              >
                {isRouteActive(value.item.key) && (
                  <div style={{ backgroundColor: theme.palette.primary.main, position: "absolute", left: -1, top: "50%", transform: "translateY(-50%)", width: 3, height: 25, borderRadius: 2 }}>
                  </div>
                )}
                <ListItemIcon
                  sx={{
                    ...NAV_ICON_SX,
                    flexDirection: isRtl ? "row-reverse" : "row",
                  }}
                >
                  <Box
                    sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                    aria-label={
                      value.item.key === "communication" && messagesUnreadCount > 0
                        ? `${messagesUnreadCount} unread messages`
                        : undefined
                    }
                  >
                    <value.item.icon
                      color={
                        isRouteActive(value.item.key) || isChildRouteActive(value.item.childeren)
                          ? "primary"
                          : "text"
                      }
                    />
                    {value.item.key === "communication" && messagesUnreadCount > 0 ? (
                      <Box
                        aria-hidden
                        sx={{
                          position: "absolute",
                          top: -2,
                          right: -2,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "error.main",
                          border: "2px solid",
                          borderColor: "background.paper",
                          animation: "commUnreadDotBlink 1.2s ease-in-out infinite",
                          "@keyframes commUnreadDotBlink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.25 },
                          },
                        }}
                      />
                    ) : null}
                  </Box>
                </ListItemIcon>
                {value.item.text !== t("moderation") ? (
                  <NavLabel text={value.item.text} />
                ) : (
                  <div dir={theme.direction} style={{ flex: 1, display: "flex" }}>
                    <Typography
                      fontWeight={600}
                      sx={{ mr: 1.5 }}
                      fontSize={"0.875rem"}
                    >
                      {value.item.text}
                    </Typography>
                  </div>
                )}
                <IconButton
                  size="small"
                  sx={{
                    flexShrink: 0,
                    alignSelf: "center",
                    p: 0.5,
                    ml: isRtl ? 0 : 0.25,
                    mr: isRtl ? 0.25 : 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(e, value.item.key);
                  }}
                >
                  {openValue[value.item.collapse] ? (
                    <ExpandLess
                      sx={{ width: 14, height: 14 }}
                      color={
                        isRouteActive(value.item.key) || isChildRouteActive(value.item.childeren)
                          ? "primary"
                          : "text"
                      }
                    />
                  ) : (
                    <ExpandMore
                      sx={{ width: 14, height: 14 }}
                      color={
                        isRouteActive(value.item.key) || isChildRouteActive(value.item.childeren)
                          ? "primary"
                          : "text"
                      }
                    />
                  )}
                </IconButton>
              </ListItemButton>
            </ListItem>
            <Collapse
              in={openValue[value.item.collapse]}
              timeout="auto"
              unmountOnExit
            >
              {value.item.childeren.map((childValue) => {
                const navTitleLines = SIDEBAR_MULTILINE_TITLES[childValue.key];
                const isTwoLineNav = Boolean(navTitleLines);
                const childSelected = isRouteActive(childValue.key);
                return (
                  <ListItem key={childValue.key} disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      data-tour={
                        {
                          messageTemplatesPermissions: "sidenav-message-templates",
                          communicationMessages: "sidenav-messages",
                          communicationMessageTemplates: "sidenav-communication-message-templates",
                          communicationEmailTemplates: "sidenav-communication-email-templates",
                          communicationCalls: "sidenav-calls",
                          communicationModes: "sidenav-communication-modes",
                        }[childValue.key]
                      }
                      onClick={(e) => handleSelected(e, childValue.key)}
                      selected={childSelected}
                      sx={{
                        ...NAV_ROW_BUTTON_SX,
                        flexDirection: isRtl ? "row-reverse" : "row",
                        alignItems: "center",
                        minHeight: 48,
                        overflow: "hidden",
                        ...subNavListItemSx,
                        borderRadius: 1,
                        pl: 5,
                        pr: 1,
                        py: 0,
                      }}
                    >
                      <SubNavSelectionMarker active={childSelected} />
                      <ListItemIcon
                        sx={{
                          ...NAV_ICON_SX,
                          alignSelf: "center",
                        }}
                      >
                        <childValue.icon
                          fontSize="small"
                          color={childSelected ? "primary" : "text"}
                        />
                      </ListItemIcon>
                      {childValue.key === "communicationMessages" ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flex: "1 1 auto",
                            minWidth: 0,
                            gap: 0.75,
                            overflow: "hidden",
                          }}
                        >
                          <SubNavLabel text={childValue.text} selected={childSelected} />
                          {messagesUnreadCount > 0 ? (
                            <Chip
                              label={messagesUnreadCount}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                fontSize: "0.7rem",
                                height: 22,
                                minHeight: 22,
                                maxWidth: "fit-content",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                              color="primary"
                            />
                          ) : null}
                        </Box>
                      ) : navTitleLines ? (
                        <Box sx={{ flex: "1 1 auto", minWidth: 0, my: 0, py: 0.125 }}>
                          {navTitleLines.map((line, idx) => (
                            <Typography
                              key={idx}
                              component="span"
                              sx={{
                                display: "block",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                lineHeight: 1.28,
                                color: childSelected ? "primary.main" : "text.primary",
                              }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <SubNavLabel text={childValue.text} selected={childSelected} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Collapse>
          </Box>
        ) : (
          <ListItem key={value.item.key} disablePadding>
            <ListItemButton
              data-tour={
                tourEnabled
                  ? {
                      dashboard: "sidenav-dashboard",
                      patients: "sidenav-patients",
                      tasks: "sidenav-tasks",
                      "case-days": "sidenav-case-days",
                      registry: "sidenav-registry",
                      contacts: "sidenav-contacts",
                      messages: "sidenav-messages",
                    }[value.item.key]
                  : undefined
              }
              onClick={(e) => handleSelected(e, value.item.key)}
              selected={isRouteActive(value.item.key)}
              sx={{
                ...NAV_ROW_BUTTON_SX,
                flexDirection: isRtl ? "row-reverse" : "row",
                ...sxListItem,
              }}
            >
              {isRouteActive(value.item.key) && (
                <div style={{ backgroundColor: theme.palette.primary.main, position: "absolute", left: -1, top: "50%", transform: "translateY(-50%)", width: 3, height: 25, borderRadius: 2 }}>
                </div>
              )}
              <ListItemIcon
                sx={{
                  ...NAV_ICON_SX,
                  flexDirection: isRtl ? "row-reverse" : "row",
                }}
              >
                <value.item.icon
                  color={
                    isRouteActive(value.item.key)
                      ? "primary"
                      : "text"
                  }
                />
              </ListItemIcon>
              <NavLabel text={value.item.text} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </>
  );
}

export default SideList;